import { Button, Collapse, Form, Input, Radio, Select } from 'antd';

import QuillEditorField from './QuillEditorField';

import '../style.scss';

const { Panel } = Collapse;

const wideFormItemProps = {
  labelCol: { span: 4 },
  wrapperCol: { span: 16 },
  labelAlign: 'left' as const,
};

const inputStyle = { width: '100%' };
const textAreaStyle = { width: '100%', minHeight: 100 };

const headingLevelOptions = [
  { label: 'H1', value: 1 },
  { label: 'H2', value: 2 },
  { label: 'H3', value: 3 },
  { label: 'H4', value: 4 },
  { label: 'H5', value: 5 },
  { label: 'H6', value: 6 },
];

/** Main title + intro for About / Guide. */
export function AboutMainSectionFields() {
  return (
    <>
      <Form.Item
        name={['aboutHeading', 'text']}
        label="Title"
        {...wideFormItemProps}
        rules={[{ required: true, message: 'Main title is required' }]}
      >
        <Input placeholder="Everything about the OCI card" style={inputStyle} />
      </Form.Item>
      <Form.Item
        name={['aboutHeading', 'highlight']}
        label="Highlighted Word"
        {...wideFormItemProps}
      >
        <Input placeholder="OCI card" style={inputStyle} />
      </Form.Item>
      <Form.Item name={['aboutHeading', 'subtitle']} label="Subtitle" {...wideFormItemProps}>
        <Input.TextArea
          rows={2}
          placeholder="Plain-English answers to who can apply, what you need, and how it works."
          style={inputStyle}
        />
      </Form.Item>
      <Form.Item
        name={['aboutHeading', 'level']}
        label="Heading Tag"
        {...wideFormItemProps}
        initialValue={2}
      >
        <Select
          style={inputStyle}
          options={headingLevelOptions}
          dropdownClassName="select-checkbox-dropdown"
        />
      </Form.Item>
      <Form.Item
        name="aboutDescription"
        label="Intro Text"
        {...wideFormItemProps}
        valuePropName="value"
        getValueFromEvent={value => value}
        extra="Shown above content blocks on the frontend About section."
      >
        <QuillEditorField className="spf-process-editor" />
      </Form.Item>
    </>
  );
}

/** Collapsible Q&A / content blocks. */
export function ContentBlocksSectionFields() {
  return (
    <Form.List name="aboutContentBlocks">
      {(fields, { add, remove }) => (
        <>
          <Collapse
            accordion
            expandIconPosition="right"
            style={{ margin: 16, background: '#fafafa' }}
          >
            {fields.map((field, index) => (
              <Panel
                key={field.key}
                header={
                  <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => {
                      const question = getFieldValue([
                        'aboutContentBlocks',
                        field.name,
                        'question',
                      ]);
                      return question || `Block ${index + 1}`;
                    }}
                  </Form.Item>
                }
                forceRender
                extra={
                  fields.length > 0 ? (
                    <Button
                      style={{ marginRight: '25px', marginTop: '5px' }}
                      danger
                      size="small"
                      onClick={event => {
                        event.stopPropagation();
                        remove(field.name);
                      }}
                    >
                      Remove
                    </Button>
                  ) : null
                }
              >
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => {
                    const blockType = getFieldValue(['aboutContentBlocks', field.name, 'type']);

                    if (blockType === 'paragraph') {
                      return (
                        <Form.Item
                          {...field}
                          name={[field.name, 'html']}
                          label="Description"
                          {...wideFormItemProps}
                          valuePropName="value"
                          getValueFromEvent={value => value}
                        >
                          <QuillEditorField className="spf-process-editor" />
                        </Form.Item>
                      );
                    }

                    return (
                      <>
                        <Form.Item
                          {...field}
                          name={[field.name, 'question']}
                          label="Title"
                          {...wideFormItemProps}
                          rules={[{ required: true, message: 'Heading is required' }]}
                        >
                          <Input placeholder="What is an OCI card?" style={inputStyle} />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, 'answerFormat']}
                          label="Type"
                          {...wideFormItemProps}
                          initialValue="html"
                        >
                          <Radio.Group>
                            <Radio value="html">Text / HTML</Radio>
                            <Radio value="checklist">Checklist</Radio>
                          </Radio.Group>
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate>
                          {({ getFieldValue: getValue }) => {
                            const answerFormat = getValue([
                              'aboutContentBlocks',
                              field.name,
                              'answerFormat',
                            ]);

                            if (answerFormat === 'checklist') {
                              return (
                                <Form.List name={[field.name, 'checklistItems']}>
                                  {(checklistFields, { add: addItem, remove: removeItem }) => (
                                    <>
                                      {checklistFields.map((checklistField, itemIndex) => (
                                        <Form.Item
                                          key={checklistField.key}
                                          label={`Checklist Item ${itemIndex + 1}`}
                                          {...wideFormItemProps}
                                        >
                                          <div
                                            style={{
                                              display: 'flex',
                                              gap: 8,
                                            }}
                                          >
                                            <Form.Item
                                              {...checklistField}
                                              name={[checklistField.name]}
                                              rules={[
                                                {
                                                  required: true,
                                                  message: 'Checklist item is required',
                                                },
                                              ]}
                                              style={{
                                                marginBottom: 0,
                                                flex: 1,
                                                padding: 0,
                                                border: 'none',
                                              }}
                                            >
                                              <Input.TextArea
                                                rows={2}
                                                placeholder="Checklist item"
                                                style={textAreaStyle}
                                              />
                                            </Form.Item>
                                            <Button
                                              danger
                                              onClick={() => removeItem(checklistField.name)}
                                            >
                                              Remove
                                            </Button>
                                          </div>
                                        </Form.Item>
                                      ))}
                                      <Form.Item wrapperCol={{ span: 12, offset: 4 }}>
                                        <Button type="dashed" onClick={() => addItem('')}>
                                          + Add Checklist Item
                                        </Button>
                                      </Form.Item>
                                    </>
                                  )}
                                </Form.List>
                              );
                            }

                            return (
                              <Form.Item
                                {...field}
                                name={[field.name, 'answer']}
                                label="Description"
                                {...wideFormItemProps}
                                valuePropName="value"
                                getValueFromEvent={value => value}
                              >
                                <QuillEditorField className="spf-process-editor" />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </>
                    );
                  }}
                </Form.Item>
              </Panel>
            ))}
          </Collapse>

          <Form.Item wrapperCol={{ span: 12, offset: 4 }} style={{ marginBottom: 0 }}>
            <Button
              type="dashed"
              onClick={() =>
                add({
                  sequence: fields.length + 1,
                  type: 'qa',
                  html: '',
                  question: '',
                  answer: '',
                  answerFormat: 'html',
                  checklistItems: [],
                })
              }
              style={{ color: '#637f92', fontWeight: 600 }}
            >
              + Add Content Block
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  );
}

/** @deprecated Prefer AboutMainSectionFields + ContentBlocksSectionFields */
export default function AboutSectionFields() {
  return (
    <>
      <AboutMainSectionFields />
      <div style={{ marginTop: 24 }}>
        <p style={{ fontWeight: 700, marginBottom: 12 }}>Content Blocks</p>
        <ContentBlocksSectionFields />
      </div>
    </>
  );
}
